import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../components/layout/Header";
import BottomNav from "../components/layout/BottomNav";
import { userService } from "../services/userService";

import { UserData } from "../types/user";
import { HiChevronLeft } from "react-icons/hi";
import {
  HiArrowLeft,
  HiCalendar,
  HiClock,
  HiPlay,
  HiStop,
  HiCheckCircle,
  HiTrash,
  HiNoSymbol,
  HiArrowPath,
  HiTag,
  HiPencil,
} from "react-icons/hi2";
import { useAuth } from "../contexts/AuthContext";
import { sessionService } from "../services/sessionService";
import { activityService } from "../services/activityService";
import { GlobalActivityService } from "../services/globalActivityService";
import {
  Session,
  ActivityMeasurement,
  PERIODISATION_OPTIONS,
} from "../types/post";
import { ActivityType, TrainingType, ActivityVariable } from "../types/activity";
import { trainingTypeService } from "../services/trainingTypeService";
import { normalizeActivityVariables } from "../utils/unitUtils";
import { asDisplayNameOrNull } from "../utils/displayName";
import {
  applyPreferenceToVariables,
  getActivityPrefKey,
} from "../utils/activityVariablePreferences";
import {
  ActivityMeasurementModal,
  EditActivityMeasurementModal,
  EditSessionModal,
} from "../components/sessions";
import TrainingLogForm from "../components/DayPost/training-log/TrainingLogForm";
import { sumSessionDurationMinutes } from "../utils/sessionRules";
import MasterSessionDetails from "../components/sessions/MasterSessionDetails";
import {
  formatElapsedTime,
  initialCollapsedGroups,
} from "../components/sessions/activityGroupCollapse";
import AILogButton, { type AILogFunction } from "../components/AILogButton";
import { SessionNotesMarkdown } from "../components/sessions/SessionNotesMarkdown";
import ConfirmDialog from "../components/ConfirmDialog";
import {
  sessionStatusChipClass,
  STATUS_CHIP_BASE,
} from "../utils/sessionStatusStyles";
import { TEXT_BUTTON_CLASS } from "../utils/buttonStyles";

const SessionPage = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // State
  const [session, setSession] = useState<Session | null>(null);

  // Keep ref in sync with session state
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [globalActivityTypes, setGlobalActivityTypes] = useState<
    Record<string, ActivityType[]>
  >({});
  const [trainingTypes, setTrainingTypes] = useState<TrainingType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [elapsedTime, setElapsedTime] = useState<number>(0); // in seconds
  const [isTimerRunning, setIsTimerRunning] = useState<boolean>(false);
  const [isStartingTimer, setIsStartingTimer] = useState<boolean>(false);
  const [isMeasurementModalOpen, setIsMeasurementModalOpen] =
    useState<boolean>(false);
  const [selectedActivityIndex, setSelectedActivityIndex] =
    useState<number>(-1);
  const [selectedActivityType, setSelectedActivityType] =
    useState<ActivityType | null>(null);
  const [isEditMeasurementModalOpen, setIsEditMeasurementModalOpen] =
    useState<boolean>(false);
  const [selectedMeasurement, setSelectedMeasurement] =
    useState<ActivityMeasurement | null>(null);
  const [selectedMeasurementIndex, setSelectedMeasurementIndex] =
    useState<number>(-1);
  const [measurementToDelete, setMeasurementToDelete] = useState<{
    measurement: ActivityMeasurement;
    index: number;
  } | null>(null);
  const [isDeletingMeasurement, setIsDeletingMeasurement] =
    useState<boolean>(false);
  const [isEndingSession, setIsEndingSession] = useState<boolean>(false);
  const [isTrainingLogFormOpen, setIsTrainingLogFormOpen] =
    useState<boolean>(false);
  const [showCompletionToast, setShowCompletionToast] = useState<{
    message: string;
    activityName: string;
  } | null>(null);
  const [completedMeasurements, setCompletedMeasurements] = useState<
    Set<number>
  >(new Set());
  const [savingMeasurements, setSavingMeasurements] = useState<Set<number>>(
    new Set(),
  );
  const [isEditSessionModalOpen, setIsEditSessionModalOpen] =
    useState<boolean>(false);
  const [sessionToDelete, setSessionToDelete] = useState<Session | null>(null);
  const [isDeletingSession, setIsDeletingSession] = useState<boolean>(false);
  const [rejectionReason, setRejectionReason] = useState<string>("");

  // Collapsed activity groups, keyed by rendered section id (one
  // MasterSessionDetails per coach block, or FLAT_SECTION_ID for legacy
  // sessions). Lifted here so every instance shares one source of truth.
  // `null` until the session first loads; each section then seeds
  // collapsed-by-default (plan 3e / decision 4).
  const [collapsedBySection, setCollapsedBySection] = useState<Record<
    string,
    Set<number>
  > | null>(null);

  // Recent activity IDs for "Add Set" buttons
  const [recentActivityIds, setRecentActivityIds] = useState<string[]>([]);
  const [lastSelectedActivityId, setLastSelectedActivityId] =
    useState<string>("");

  // AI dictation state
  const [aiPrompt, setAiPrompt] = useState<string>("");
  const [aiLogFunctions, setAiLogFunctions] = useState<AILogFunction[]>([]);

  const handleAIConfigReady = useCallback(
    (prompt: string, logFunctions: AILogFunction[]) => {
      setAiPrompt(prompt);
      setAiLogFunctions(logFunctions);
    },
    [],
  );

  // Persist AI dictation changes to the backend the same way manual edits
  // do: update local state optimistically, then push the full new
  // ActivityMeasurementList to Firestore so log times and value changes
  // survive a refresh. Throws on failure so the AI surfaces an error.
  const handleAIApplyChanges = useCallback(
    async (updatedMeasurements: ActivityMeasurement[]) => {
      if (!currentUser?.uid || !sessionId) {
        throw new Error("Not signed in or no session loaded.");
      }
      const baseSession = sessionRef.current;
      if (!baseSession) {
        throw new Error("Session is not loaded yet.");
      }

      const updatedSession = {
        ...baseSession,
        ActivityMeasurementList: updatedMeasurements,
      };
      setSession(updatedSession);
      sessionRef.current = updatedSession;

      try {
        await sessionService.updateSession(currentUser.uid, sessionId, {
          ActivityMeasurementList: updatedMeasurements,
        });
      } catch (err) {
        console.error("Failed to persist AI changes:", err);
        setError("Failed to save AI changes");
        throw err;
      }
    },
    [currentUser?.uid, sessionId],
  );

  // Refs for debouncing
  const debounceTimers = useRef<Map<string, NodeJS.Timeout>>(new Map());
  // Activity list as it was when Start was pressed in this visit, restored
  // on Cancel so the attempt's logged values are discarded.
  const preStartMeasurements = useRef<ActivityMeasurement[] | null>(null);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isCancellingSession, setIsCancellingSession] = useState(false);
  const pendingSaves = useRef<Map<string, Promise<void>>>(new Map());
  const sessionRef = useRef<Session | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);

  // Load recent activity IDs from localStorage on component mount
  useEffect(() => {
    if (currentUser?.uid) {
      const savedRecentIds = localStorage.getItem(
        `recentActivityIds_${currentUser.uid}`,
      );
      if (savedRecentIds) {
        try {
          const parsedIds = JSON.parse(savedRecentIds);
          if (Array.isArray(parsedIds)) {
            setRecentActivityIds(parsedIds.slice(0, 5));
          }
        } catch (e) {
          console.warn("Failed to parse saved recent activity IDs:", e);
        }
      }
    }
  }, [currentUser?.uid]);

  // Save recent activity IDs to localStorage
  useEffect(() => {
    if (currentUser?.uid && recentActivityIds.length > 0) {
      localStorage.setItem(
        `recentActivityIds_${currentUser.uid}`,
        JSON.stringify(recentActivityIds),
      );
    }
  }, [recentActivityIds, currentUser?.uid]);

  // Fetch session and activity types data
  const fetchData = async () => {
    if (!currentUser?.uid || !sessionId) return;

    try {
      const { userData } = await userService.getUserProfile(currentUser.uid);
      setUserData(userData);
      setLoading(true);
      setError(null);

      // Fetch session data (handles both regular and proposed sessions)
      const sessionData = await sessionService.getSession(
        currentUser.uid,
        sessionId,
      );

      if (!sessionData) {
        throw new Error("Session not found");
      }

      const [activityTypesData, trainingTypesData] = await Promise.all([
        sessionData.coachIssued && sessionData.organisationID
          ? activityService.fetchCoachActivityTypes(sessionData.organisationID)
          : activityService.fetchActivityTypes(currentUser.uid),
        trainingTypeService.fetchTrainingTypes(),
      ]);
      setActivityTypes(activityTypesData);
      setTrainingTypes(trainingTypesData);

      setSession(sessionData);

      // Add existing session activity types to recent activities for "Add Set" buttons
      if (sessionData?.ActivityMeasurementList) {
        const existingActivityIds = Array.from(
          new Set(
            sessionData.ActivityMeasurementList.map(
              (measurement: ActivityMeasurement) => measurement.ActivityTypeID,
            ),
          ),
        );
        setRecentActivityIds((prev) => {
          const combined = [...existingActivityIds, ...prev];
          return Array.from(new Set(combined)).slice(0, 5);
        });
      }
    } catch (error) {
      console.error("Error fetching session data:", error);
      setError("Failed to load session");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchAllGlobalActivitiesForLookup = async () => {
      if (!currentUser?.uid) return;
      try {
        const categories = await GlobalActivityService.fetchCategories();
        const categoryIds = categories
          .map((category) => category.id)
          .filter((id): id is string => !!id);

        if (categoryIds.length === 0) return;

        const allGlobalTypes =
          await GlobalActivityService.fetchActivityTypesForCategories(
            categoryIds,
          );

        const grouped = allGlobalTypes.reduce<Record<string, ActivityType[]>>(
          (acc, type) => {
            const categoryId = type.categoryId;
            if (!categoryId) return acc;
            if (!acc[categoryId]) acc[categoryId] = [];
            acc[categoryId].push({
              id: type.id,
              name: type.name,
              description: type.description,
              variables: type.variables,
              image: type.image,
              isFromPack: true,
              categoryId: type.categoryId,
            });
            return acc;
          },
          {},
        );
        setGlobalActivityTypes(grouped);
      } catch (error) {
        console.error(
          "Error preloading global activities for session page:",
          error,
        );
      }
    };

    fetchAllGlobalActivitiesForLookup();
  }, [currentUser?.uid]);

  const getRawActivityId = (activityTypeId: string): string => {
    if (activityTypeId.startsWith("global_")) {
      const parts = activityTypeId.split("_");
      if (parts.length >= 3) {
        return parts.slice(2).join("_");
      }
    }
    return activityTypeId;
  };

  const getGlobalCategoryIdFromActivityTypeId = (
    activityTypeId: string,
  ): string | undefined => {
    if (!activityTypeId.startsWith("global_")) return undefined;
    const parts = activityTypeId.split("_");
    if (parts.length >= 3) {
      return parts[1];
    }
    return undefined;
  };

  const resolveGlobalActivityType = (
    activityTypeId: string,
    globalCategoryID?: string,
  ): ActivityType | undefined => {
    const rawActivityId = getRawActivityId(activityTypeId);
    const categoryId =
      globalCategoryID || getGlobalCategoryIdFromActivityTypeId(activityTypeId);

    if (categoryId) {
      const categoryTypes = globalActivityTypes[categoryId] || [];
      const match = categoryTypes.find((type) => type.id === rawActivityId);
      if (match) return match;
    }

    for (const categoryTypes of Object.values(globalActivityTypes)) {
      const match = categoryTypes.find((type) => type.id === rawActivityId);
      if (match) return match;
    }

    return undefined;
  };

  const resolveActivityTypeFromMeasurement = (
    measurement: ActivityMeasurement,
  ): ActivityType | undefined => {
    const rawActivityId = getRawActivityId(measurement.ActivityTypeID);
    const userMatch = activityTypes.find((type) => type.id === rawActivityId);
    if (userMatch) return userMatch;
    return resolveGlobalActivityType(
      measurement.ActivityTypeID,
      measurement.globalCategoryID,
    );
  };

  // Get the first activity name from the session
  const getFirstActivityName = (): string | null => {
    if (
      !session?.ActivityMeasurementList ||
      session.ActivityMeasurementList.length === 0
    ) {
      return null;
    }
    const firstMeasurement = session.ActivityMeasurementList[0];
    const activityType = resolveActivityTypeFromMeasurement(firstMeasurement);
    return activityType?.name || null;
  };

  // Get the training type name (prefers trainingTypeID, falls back to
  // resolving the legacy composite sessionTypeID)
  const getSessionTypeName = (): string | null => {
    if (!session) return null;

    const resolved = trainingTypeService.getSessionTrainingTypeName(
      session,
      trainingTypes,
    );
    if (resolved) return resolved;

    // Last-resort fallback for legacy composite IDs whose name no longer
    // matches any training type: show the raw parsed name.
    const legacy = session.sessionTypeID?.trim();
    if (legacy?.includes(":")) {
      return legacy.split(":")[1] || null;
    }
    return null;
  };

  // Get the periodisation label
  const getPeriodisationLabel = (): string | null => {
    if (!session?.periodisation) return null;
    const option = PERIODISATION_OPTIONS.find(
      (opt) => opt.value === session.periodisation,
    );
    return option?.label || session.periodisation;
  };

  // Get the display title (priority: name > session type > first activity)
  const getDisplayTitle = (resolvedSessionTypeName?: string | null): string => {
    const safeName = asDisplayNameOrNull(session?.name);
    if (safeName) {
      return safeName;
    }
    const sessionTypeName = resolvedSessionTypeName ?? getSessionTypeName();
    if (sessionTypeName) {
      return sessionTypeName;
    }
    const firstActivityName = getFirstActivityName();
    if (firstActivityName) {
      return firstActivityName;
    }
    return `Session - ${formatDate(session?.plannedDate || "")}`;
  };

  useEffect(() => {
    fetchData();
  }, [currentUser?.uid, sessionId]);

  // Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      debounceTimers.current.forEach((timer) => {
        clearTimeout(timer);
      });
      debounceTimers.current.clear();
    };
  }, []);

  // Timer effect - updates elapsed time every second when timer is running
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (isTimerRunning && session?.startTime) {
      interval = setInterval(() => {
        const startTime = new Date(session.startTime!).getTime();
        const now = Date.now();
        const elapsed = Math.floor((now - startTime) / 1000);
        setElapsedTime(elapsed);
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isTimerRunning, session?.startTime]);

  // Seed collapsed-by-default once, the first time the session has activities.
  useEffect(() => {
    if (collapsedBySection !== null || !session) return;
    const flat = session.ActivityMeasurementList || [];
    if (flat.length === 0) return;
    const blocks = session.activityBlocks || [];
    const seeded: Record<string, Set<number>> = {};
    if (blocks.length === 0) {
      seeded["__flat__"] = initialCollapsedGroups(flat);
    } else {
      const used = new Set<number>();
      blocks.forEach((block) => {
        if (block.blockType === "notes") return;
        const rows = flat.filter((m, i) => {
          if (m.blockId !== block.id) return false;
          used.add(i);
          return true;
        });
        if (rows.length) seeded[block.id] = initialCollapsedGroups(rows);
      });
      const leftover = flat.filter((_, i) => !used.has(i));
      if (leftover.length)
        seeded["__leftover__"] = initialCollapsedGroups(leftover);
    }
    setCollapsedBySection(seeded);
  }, [session, collapsedBySection]);

  // Initialize timer state when session loads
  useEffect(() => {
    if (session) {
      const isExecuting = session.status === "executing";
      const hasStartTime = !!session.startTime;

      if (isExecuting && hasStartTime) {
        setIsTimerRunning(true);
        // Calculate initial elapsed time
        const startTime = new Date(session.startTime!).getTime();
        const now = Date.now();
        const elapsed = Math.floor((now - startTime) / 1000);
        setElapsedTime(elapsed);
      } else {
        setIsTimerRunning(false);
        setElapsedTime(0);
      }
    }
  }, [session]);

  // Update recent activities when lastSelectedActivityId changes
  useEffect(() => {
    if (
      lastSelectedActivityId &&
      !recentActivityIds.includes(lastSelectedActivityId)
    ) {
      setRecentActivityIds((prev) => {
        const updated = [lastSelectedActivityId, ...prev].slice(0, 5);
        return updated;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastSelectedActivityId]);

  // Start timer function
  const handleStartTimer = async () => {
    if (!currentUser?.uid || !sessionId || !session) return;

    try {
      setIsStartingTimer(true);
      setError(null);
      preStartMeasurements.current = JSON.parse(
        JSON.stringify(session.ActivityMeasurementList || []),
      );

      // Update session to executing status with start time
      await sessionService.startSession(currentUser.uid, sessionId);

      // Refresh session data to get the updated startTime
      await fetchData();

      console.log("Timer started successfully");
    } catch (error) {
      console.error("Error starting timer:", error);
      setError("Failed to start timer");
    } finally {
      setIsStartingTimer(false);
    }
  };

  // Handle activity click to open measurement modal
  const handleActivityClick = (measurementIndex: number) => {
    if (!session) return;
    const measurement = session.ActivityMeasurementList?.[measurementIndex];
    if (!measurement) {
      setError("Measurement not found");
      return;
    }

    const activityType = resolveActivityTypeFromMeasurement(measurement);
    if (!activityType) {
      setError("Exercise not found");
      return;
    }

    setSelectedActivityIndex(measurementIndex);
    setSelectedActivityType(activityType);
    setIsMeasurementModalOpen(true);
  };

  // Handle measurement creation - update planned measurement with logged values
  const handleMeasurementCreated = async (
    updatedVariables: ActivityVariable[],
  ) => {
    if (!currentUser?.uid || !sessionId || selectedActivityIndex < 0) return;

    try {
      setError(null);

      await sessionService.addActivityMeasurement(
        currentUser.uid,
        sessionId,
        selectedActivityIndex,
        updatedVariables,
      );

      // Refresh session data to get updated measurements
      await fetchData();

      // Refresh activity types
      try {
        const updatedActivityTypes = await activityService.fetchActivityTypes(
          currentUser.uid,
        );
        setActivityTypes(updatedActivityTypes);
      } catch (error) {
        console.error("Error refreshing activity types:", error);
      }

      console.log("Activity measurement created successfully");
    } catch (error) {
      console.error("Error creating measurement:", error);
      setError("Failed to log exercise measurement");
    }
  };
const handlePlannedMeasurementsChange = useCallback(
  (updatedMeasurements: ActivityMeasurement[]) => {
    setSession((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, ActivityMeasurementList: updatedMeasurements };
      sessionRef.current = updated;
      return updated;
    });
  },
  [], // no deps needed since we use the functional setSession form
);
  // Handle edit measurement
  const handleEditMeasurement = (
    measurement: ActivityMeasurement,
    measurementIndex: number,
  ) => {
    const activityType = resolveActivityTypeFromMeasurement(measurement);
    if (!activityType) {
      setError("Exercise not found");
      return;
    }

    setSelectedMeasurement(measurement);
    setSelectedMeasurementIndex(measurementIndex);
    setSelectedActivityType(activityType);
    setIsEditMeasurementModalOpen(true);
  };

  // Handle measurement update
  const handleMeasurementUpdated = async (
    measurementIndex: number,
    updatedMeasurement: Partial<ActivityMeasurement>,
    skipRefresh = false,
  ) => {
    if (!currentUser?.uid || !sessionId) return;

    try {
      setError(null);

      await sessionService.updateActivityMeasurement(
        currentUser.uid,
        sessionId,
        measurementIndex,
        updatedMeasurement,
      );

      if (!skipRefresh) {
        // Refresh session data to get updated measurements
        await fetchData();

        // Also refresh activity types in case new labels were added
        try {
          const updatedActivityTypes = await activityService.fetchActivityTypes(
            currentUser.uid,
          );
          setActivityTypes(updatedActivityTypes);
        } catch (error) {
          console.error("Error refreshing activity types:", error);
        }
      }

      console.log("Activity measurement updated successfully");
    } catch (error) {
      console.error("Error updating measurement:", error);
      setError("Failed to update exercise measurement");
    }
  };

  // Handle variable update (inline editing with debouncing)
  const handleVariableUpdate = (
    measurementIndex: number,
    variableIndex: number,
    value: number | null,
    unit?: string,
    shouldAutoFocus: boolean = false,
  ) => {
    if (!currentUser?.uid || !sessionId || !session) return;

    const measurement = session.ActivityMeasurementList?.[measurementIndex];
    if (!measurement) return;

    // Update local state immediately (optimistic update)
    const updatedVariables = [...measurement.variables];
    updatedVariables[variableIndex] = {
      ...updatedVariables[variableIndex],
      measurement: value,
      ...(unit !== undefined && { unit }), // Only update unit if provided
    };

    // Check if all visible variables are filled (hidden columns are set by the
    // coach and cannot be edited by the athlete, so they don't gate completion).
    const allVariablesFilled = updatedVariables
      .filter((v) => !v.hidden)
      .every((v) => v.measurement !== null && v.measurement !== undefined);

    // Update local session state immediately
    const updatedMeasurements = [...(session.ActivityMeasurementList || [])];
    const updatedMeasurement: ActivityMeasurement = {
      ...updatedMeasurements[measurementIndex],
      variables: updatedVariables,
    };

    // If all variables are filled, set timeLogged and mark as saving
    // If not all filled, remove timeLogged
    const wasComplete = !!measurement.timeLogged;
    if (allVariablesFilled) {
      if (!updatedMeasurement.timeLogged) {
        updatedMeasurement.timeLogged = new Date().toISOString();

        // Add to saving set if this is a new completion
        if (!wasComplete) {
          setSavingMeasurements((prev) => new Set(prev).add(measurementIndex));
        }
      }
    } else {
      // If fields were cleared, remove timeLogged
      updatedMeasurement.timeLogged = undefined;
      // Remove from saving and completed measurements if it was there
      setSavingMeasurements((prev) => {
        const newSet = new Set(prev);
        newSet.delete(measurementIndex);
        return newSet;
      });
      setCompletedMeasurements((prev) => {
        const newSet = new Set(prev);
        newSet.delete(measurementIndex);
        return newSet;
      });
    }

    updatedMeasurements[measurementIndex] = updatedMeasurement;
    setSession({
      ...session,
      ActivityMeasurementList: updatedMeasurements,
    });

    // Auto-focus on next incomplete field if all variables in current measurement are filled
    if (shouldAutoFocus && allVariablesFilled) {
      setTimeout(() => {
        // Find next incomplete measurement
        const nextIncompleteIndex = updatedMeasurements.findIndex(
          (m, idx) => idx > measurementIndex && !isMeasurementComplete(m),
        );

        if (nextIncompleteIndex !== -1) {
          const nextMeasurement = updatedMeasurements[nextIncompleteIndex];
          const firstEmptyIndex = nextMeasurement.variables.findIndex(
            (v) => v.measurement === null || v.measurement === undefined,
          );
          if (firstEmptyIndex !== -1) {
            const inputId = `measurement-${nextIncompleteIndex}-variable-${firstEmptyIndex}`;
            const input = document.getElementById(inputId);
            if (input) {
              input.focus();
            }
          }
        }
      }, 100);
    }

    // Clear existing debounce timer for this measurement (per measurement, not per variable)
    const timerKey = `measurement-${measurementIndex}`;
    const existingTimer = debounceTimers.current.get(timerKey);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    // Set up new debounce timer (2 seconds)
    const timer = setTimeout(async () => {
      // Read latest session state from ref when save fires
      const latestSession = sessionRef.current;
      if (!latestSession?.ActivityMeasurementList?.[measurementIndex]) {
        debounceTimers.current.delete(timerKey);
        return;
      }

      const latestMeasurement =
        latestSession.ActivityMeasurementList[measurementIndex];
      const latestVariables = latestMeasurement.variables;
      const allFilled = latestVariables
        .filter((v) => !v.hidden)
        .every((v) => v.measurement !== null && v.measurement !== undefined);

      // Prepare update for API using latest state
      const updates: Partial<ActivityMeasurement> = {
        variables: latestVariables,
      };

      // If all variables are filled, set timeLogged (use existing or set new)
      // If not all filled, do not include timeLogged in the update payload
      if (allFilled) {
        updates.timeLogged =
          latestMeasurement.timeLogged || new Date().toISOString();
      }

      // Create a promise for this save operation
      const savePromise = (async () => {
        try {
          await handleMeasurementUpdated(measurementIndex, updates, true);

          // After successful save, remove from saving set and show completion animation
          setSavingMeasurements((prev) => {
            const newSet = new Set(prev);
            newSet.delete(measurementIndex);
            return newSet;
          });

          // Show completion toast and animation if all fields are filled
          if (allFilled) {
            const activityType =
              resolveActivityTypeFromMeasurement(latestMeasurement);
            setShowCompletionToast({
              message: "Exercise logged successfully",
              activityName: activityType?.name || "Exercise",
            });
            setTimeout(() => setShowCompletionToast(null), 3000);

            // Add completion animation
            setCompletedMeasurements((prev) =>
              new Set(prev).add(measurementIndex),
            );
            setTimeout(() => {
              setCompletedMeasurements((prev) => {
                const newSet = new Set(prev);
                newSet.delete(measurementIndex);
                return newSet;
              });
            }, 2000);
          }
        } catch (error) {
          console.error("Error updating variable:", error);
          setError("Failed to update measurement");
          // Remove from saving set on error
          setSavingMeasurements((prev) => {
            const newSet = new Set(prev);
            newSet.delete(measurementIndex);
            return newSet;
          });
          // Note: Not refreshing to avoid scroll jump - user can manually refresh if needed
        } finally {
          debounceTimers.current.delete(timerKey);
          pendingSaves.current.delete(timerKey);
        }
      })();

      pendingSaves.current.set(timerKey, savePromise);
    }, 2000); // 2 second debounce

    debounceTimers.current.set(timerKey, timer);
  };

  // Check if all visible variables in a measurement are filled. Hidden
  // variables are coach-only and aren't required for completion.
  const isMeasurementComplete = (measurement: ActivityMeasurement) => {
    return measurement.variables
      .filter((v) => !v.hidden)
      .every((v) => v.measurement !== null && v.measurement !== undefined);
  };

  // Save all measurements to database - simple synchronous approach
  const saveAllMeasurementsToDatabase = async () => {
    if (!currentUser?.uid || !sessionId) return;

    const currentSession = sessionRef.current;
    if (!currentSession?.ActivityMeasurementList) return;

    console.log("💾 Saving all session measurements to database...");

    // Clear any pending debounced saves
    debounceTimers.current.forEach((timer) => clearTimeout(timer));
    debounceTimers.current.clear();
    pendingSaves.current.clear();

    let savedCount = 0;

    // Save each measurement one by one
    for (let i = 0; i < currentSession.ActivityMeasurementList.length; i++) {
      const measurement = currentSession.ActivityMeasurementList[i];

      // Check if this measurement has any data
      const hasData = measurement.variables.some(
        (v) => v.measurement !== null && v.measurement !== undefined,
      );

      if (!hasData) {
        console.log(`  Measurement ${i + 1}: Empty, skipping`);
        continue;
      }

      const isComplete = measurement.variables
        .filter((v) => !v.hidden)
        .every((v) => v.measurement !== null && v.measurement !== undefined);

      // Prepare the update
      const updates: Partial<ActivityMeasurement> = {
        variables: measurement.variables,
        timeLogged: isComplete
          ? measurement.timeLogged || new Date().toISOString()
          : measurement.timeLogged,
      };

      console.log(
        `  Measurement ${
          i + 1
        }: Saving... (Complete: ${isComplete}, TimeLogged: ${
          updates.timeLogged ? "Yes" : "No"
        })`,
      );

      try {
        await sessionService.updateActivityMeasurement(
          currentUser.uid,
          sessionId,
          i,
          updates,
        );
        savedCount++;
        console.log(`  ✓ Measurement ${i + 1} saved`);
      } catch (error) {
        console.error(`  ✗ Measurement ${i + 1} failed:`, error);
      }
    }

    console.log(`✓ Save complete: ${savedCount} measurements saved`);

    // Clear saving indicators
    setSavingMeasurements(new Set());
  };

  // Handle delete measurement click
  const handleDeleteMeasurementClick = (
    measurement: ActivityMeasurement,
    measurementIndex: number,
  ) => {
    setMeasurementToDelete({ measurement, index: measurementIndex });
  };

  // Handle delete measurement confirmation
  const handleDeleteMeasurement = async () => {
    if (!currentUser?.uid || !sessionId || !measurementToDelete) return;

    try {
      setIsDeletingMeasurement(true);
      setError(null);

      await sessionService.deleteActivityMeasurement(
        currentUser.uid,
        sessionId,
        measurementToDelete.index,
      );

      // Refresh session data
      await fetchData();

      setMeasurementToDelete(null);
      console.log("Activity measurement deleted successfully");
    } catch (error) {
      console.error("Error deleting measurement:", error);
      setError("Failed to delete exercise measurement");
    } finally {
      setIsDeletingMeasurement(false);
    }
  };

  // Handle cancel delete
  const handleCancelDeleteMeasurement = () => {
    setMeasurementToDelete(null);
  };

  // Handle add activity from ActivitySelector
  const handleAddActivityFromSelector = async (
    activityType: ActivityType,
    insertAfterIndex?: number,
  ) => {
    if (!currentUser?.uid || !sessionId || !session) return;

    try {
      setError(null);

      // Ensure user-created activity types are in local cache (pack activities resolve via globalCategoryID)
      if (
        activityType.id &&
        !activityType.isFromPack &&
        !activityTypes.find((t) => t.id === activityType.id)
      ) {
        setActivityTypes((prev) => [...prev, activityType]);
      }

      // Pack activities arrive as `global_{categoryId}_{realId}` from ActivitySelector.
      // Store raw ActivityTypeID + globalCategoryID so MasterSessionDetails can resolve names.
      let activityTypeId = activityType.id!;
      let globalCategoryID: string | undefined;
      if (activityType.isFromPack && activityType.categoryId) {
        const parts = activityTypeId.split("_");
        if (parts.length >= 3) {
          activityTypeId = parts.slice(2).join("_");
        }
        globalCategoryID = activityType.categoryId;
      }

      // Create ActivityMeasurement with variables initialized from activityType.variables
      const normalizedVariables = normalizeActivityVariables(
        activityType.variables,
      );
      let variables: ActivityVariable[] = normalizedVariables.map(
        (variable) => ({
          name: variable.name,
          measurement: null,
          unit: variable.defaultUnit,
        }),
      );

      // Apply the athlete's saved variable preferences (order/unit/hidden) for
      // self-created sessions only. Coach-assigned sessions keep the coach config.
      const sessionIsCoachAssigned = Boolean(
        session.coachIssued || session.coachID || session.programLinkage,
      );
      if (!sessionIsCoachAssigned) {
        variables = applyPreferenceToVariables(
          variables,
          userData?.activityVariablePreferences?.[
            getActivityPrefKey({
              activityTypeId,
              globalCategoryId: globalCategoryID,
            })
          ],
        );
      }

      const newMeasurement: ActivityMeasurement = {
        ActivityTypeID: activityTypeId,
        ...(activityType.name && { activityName: activityType.name }),
        variables: variables,
        ...(globalCategoryID && { globalCategoryID }),
      };

      // Update local state immediately (optimistic update)
      const currentMeasurements = session.ActivityMeasurementList || [];
      const updatedMeasurements =
        insertAfterIndex !== undefined
          ? [
              ...currentMeasurements.slice(0, insertAfterIndex + 1),
              newMeasurement,
              ...currentMeasurements.slice(insertAfterIndex + 1),
            ]
          : [...currentMeasurements, newMeasurement];

      const updatedSession = {
        ...session,
        ActivityMeasurementList: updatedMeasurements,
      };
      setSession(updatedSession);
      sessionRef.current = updatedSession;

      // Update recent activities
      setLastSelectedActivityId(activityType.id!);

      // Save to database in background
      await sessionService.updateSession(currentUser.uid, sessionId, {
        ActivityMeasurementList: updatedMeasurements,
      });

      console.log("Activity added successfully");
    } catch (error) {
      console.error("Error adding activity:", error);
      setError("Failed to add exercise");
    }
  };

  // Handle bulk add activities from ActivitySelector
  const handleBulkAddActivitiesFromSelector = async (
    activityTypesToAdd: ActivityType[],
    insertAfterIndex?: number,
  ) => {
    if (!currentUser?.uid || !sessionId || !session) return;

    try {
      setError(null);

      // Ensure user-created activity types are in local cache (pack activities resolve via globalCategoryID)
      const missingTypes = activityTypesToAdd.filter(
        (at) =>
          at.id && !at.isFromPack && !activityTypes.find((t) => t.id === at.id),
      );
      if (missingTypes.length > 0) {
        setActivityTypes((prev) => [...prev, ...missingTypes]);
      }

      // Apply the athlete's saved variable preferences for self-created sessions
      // only. Coach-assigned sessions keep the coach config.
      const sessionIsCoachAssigned = Boolean(
        session.coachIssued || session.coachID || session.programLinkage,
      );

      const newMeasurements: ActivityMeasurement[] = activityTypesToAdd.map(
        (activityType) => {
          let activityTypeId = activityType.id!;
          let globalCategoryID: string | undefined;
          if (activityType.isFromPack && activityType.categoryId) {
            const parts = activityTypeId.split("_");
            if (parts.length >= 3) {
              activityTypeId = parts.slice(2).join("_");
            }
            globalCategoryID = activityType.categoryId;
          }

          const normalizedVariables = normalizeActivityVariables(
            activityType.variables,
          );
          let variables: ActivityVariable[] = normalizedVariables.map(
            (variable) => ({
              name: variable.name,
              measurement: null,
              unit: variable.defaultUnit,
            }),
          );

          if (!sessionIsCoachAssigned) {
            variables = applyPreferenceToVariables(
              variables,
              userData?.activityVariablePreferences?.[
                getActivityPrefKey({
                  activityTypeId,
                  globalCategoryId: globalCategoryID,
                })
              ],
            );
          }

          return {
            ActivityTypeID: activityTypeId,
            ...(activityType.name && { activityName: activityType.name }),
            variables: variables,
            ...(globalCategoryID && { globalCategoryID }),
          };
        },
      );

      // Update local state immediately (optimistic update)
      const currentMeasurements = session.ActivityMeasurementList || [];
      const updatedMeasurements =
        insertAfterIndex !== undefined
          ? [
              ...currentMeasurements.slice(0, insertAfterIndex + 1),
              ...newMeasurements,
              ...currentMeasurements.slice(insertAfterIndex + 1),
            ]
          : [...currentMeasurements, ...newMeasurements];

      const updatedSession = {
        ...session,
        ActivityMeasurementList: updatedMeasurements,
      };
      setSession(updatedSession);
      sessionRef.current = updatedSession;

      // Update last selected if only one was added
      if (activityTypesToAdd.length === 1) {
        setLastSelectedActivityId(activityTypesToAdd[0].id!);
      }

      // Save to database in background
      await sessionService.updateSession(currentUser.uid, sessionId, {
        ActivityMeasurementList: updatedMeasurements,
      });

      console.log("Bulk activities added successfully");
    } catch (error) {
      console.error("Error adding bulk activities:", error);
      setError("Failed to add exercises");
    }
  };

  // Handle duplicate activity
  const handleDuplicateActivity = async (index: number) => {
    if (!currentUser?.uid || !sessionId || !session) return;

    const measurementToDuplicate = session.ActivityMeasurementList?.[index];
    if (!measurementToDuplicate) return;

    try {
      setError(null);

      // Create a deep copy of the measurement with all its variables including units
      const duplicatedVariables = measurementToDuplicate.variables.map(
        (variable) => ({
          name: variable.name,
          measurement: variable.measurement,
          unit: variable.unit, // Preserve the unit from original
          ...(variable.hidden ? { hidden: true } : {}), // Preserve coach-hidden flag
        }),
      );

      // Check if all visible variables are filled (hidden columns are coach-only).
      const allVariablesFilled = duplicatedVariables
        .filter((v) => !v.hidden)
        .every((v) => v.measurement !== null && v.measurement !== undefined);

      // Create the duplicated measurement
      const duplicatedMeasurement: ActivityMeasurement = {
        ActivityTypeID: measurementToDuplicate.ActivityTypeID,
        ...(measurementToDuplicate.activityName && {
          activityName: measurementToDuplicate.activityName,
        }),
        variables: duplicatedVariables,
        // If all variables are filled, automatically log the duplicated measurement
        ...(allVariablesFilled && { timeLogged: new Date().toISOString() }),
      };

      // Update local state immediately (optimistic update)
      // Insert the duplicated measurement right after the original
      const updatedMeasurements = [...(session.ActivityMeasurementList || [])];
      updatedMeasurements.splice(index + 1, 0, duplicatedMeasurement);

      const updatedSession = {
        ...session,
        ActivityMeasurementList: updatedMeasurements,
      };
      setSession(updatedSession);
      sessionRef.current = updatedSession;

      // Save to database in background
      await sessionService.updateSession(currentUser.uid, sessionId, {
        ActivityMeasurementList: updatedMeasurements,
      });

      // Show completion toast if the duplicated measurement was auto-logged
      if (allVariablesFilled) {
        const activityType = resolveActivityTypeFromMeasurement(
          measurementToDuplicate,
        );
        setShowCompletionToast({
          message: "Exercise duplicated and logged successfully",
          activityName: activityType?.name || "Exercise",
        });
        setTimeout(() => setShowCompletionToast(null), 3000);

        // Add completion animation for the new measurement (index + 1)
        setCompletedMeasurements((prev) => new Set(prev).add(index + 1));
        setTimeout(() => {
          setCompletedMeasurements((prev) => {
            const newSet = new Set(prev);
            newSet.delete(index + 1);
            return newSet;
          });
        }, 2000);
      }

      console.log(
        "Activity duplicated successfully",
        allVariablesFilled ? "(auto-logged)" : "",
      );
    } catch (error) {
      console.error("Error duplicating activity:", error);
      setError("Failed to duplicate exercise");
      // Note: Not reverting optimistic update to avoid scroll jump - user can manually refresh if needed
    }
  };

  // Handle recent activity add (Add Set functionality)
  const handleRecentActivityAdd = async (
    activityId: string,
    insertAfterIndex?: number,
    globalCategoryID?: string,
  ) => {
    if (!currentUser?.uid || !sessionId || !session?.ActivityMeasurementList)
      return;

    try {
      setError(null);

      // Prefer copying structure/units from an existing measurement in the same group.
      // This is required for pack activities, which are not in the user activityTypes list.
      const existingMeasurement = session.ActivityMeasurementList.find(
        (m) =>
          m.ActivityTypeID === activityId &&
          m.globalCategoryID === globalCategoryID,
      );

      let variables: ActivityVariable[];
      let resolvedActivityName: string | undefined;

      if (existingMeasurement) {
        resolvedActivityName = existingMeasurement.activityName;
        variables = existingMeasurement.variables.map((variable) => ({
          name: variable.name,
          measurement: null,
          unit: variable.unit,
        }));
      } else {
        let activityType = activityTypes.find((type) => type.id === activityId);
        if (!activityType && globalCategoryID) {
          activityType = resolveGlobalActivityType(
            activityId,
            globalCategoryID,
          );
        }
        if (!activityType) {
          setError("Exercise not found");
          return;
        }

        resolvedActivityName = activityType.name;

        const normalizedVariables = normalizeActivityVariables(
          activityType.variables,
        );
        variables = normalizedVariables.map((variable) => ({
          name: variable.name,
          measurement: null,
          unit: variable.defaultUnit,
        }));

        // Apply the athlete's saved variable preferences for self-created
        // sessions only. Coach-assigned sessions keep the coach config.
        const sessionIsCoachAssigned = Boolean(
          session.coachIssued || session.coachID || session.programLinkage,
        );
        if (!sessionIsCoachAssigned) {
          variables = applyPreferenceToVariables(
            variables,
            userData?.activityVariablePreferences?.[
              getActivityPrefKey({
                activityTypeId: activityId,
                globalCategoryId: globalCategoryID,
              })
            ],
          );
        }
      }

      const newMeasurement: ActivityMeasurement = {
        ActivityTypeID: activityId,
        ...(resolvedActivityName && { activityName: resolvedActivityName }),
        variables: variables,
        ...(globalCategoryID && { globalCategoryID }),
      };

      let insertIndex: number;

      if (insertAfterIndex !== undefined) {
        // Insert right after the specified index (for specific group)
        insertIndex = insertAfterIndex + 1;
      } else {
        // Find the last index of a measurement with the same ActivityTypeID (fallback behavior)
        insertIndex = session.ActivityMeasurementList.length;
        for (let i = session.ActivityMeasurementList.length - 1; i >= 0; i--) {
          if (
            session.ActivityMeasurementList[i].ActivityTypeID === activityId
          ) {
            insertIndex = i + 1;
            break;
          }
        }
      }

      // Insert the new measurement at the calculated position
      const updatedMeasurements = [...session.ActivityMeasurementList];
      updatedMeasurements.splice(insertIndex, 0, newMeasurement);

      // Update local state immediately (optimistic update)
      const updatedSession = {
        ...session,
        ActivityMeasurementList: updatedMeasurements,
      };
      setSession(updatedSession);
      sessionRef.current = updatedSession;

      // Update recent activities
      setLastSelectedActivityId(activityId);

      // Save to database in background
      await sessionService.updateSession(currentUser.uid, sessionId, {
        ActivityMeasurementList: updatedMeasurements,
      });

      console.log("Set added successfully");
    } catch (error) {
      console.error("Error adding set:", error);
      setError("Failed to add set");
      // Note: Not reverting optimistic update to avoid scroll jump - user can manually refresh if needed
    }
  };

  // Handle remove activity
  const handleRemoveActivity = async (index: number) => {
    if (!currentUser?.uid || !sessionId || !session) return;

    const measurementToRemove = session.ActivityMeasurementList?.[index];
    if (!measurementToRemove) return;

    try {
      setError(null);

      // Update local state immediately (optimistic update)
      const updatedMeasurements = (
        session.ActivityMeasurementList || []
      ).filter((_, i) => i !== index);

      const updatedSession = {
        ...session,
        ActivityMeasurementList: updatedMeasurements,
      };
      setSession(updatedSession);
      sessionRef.current = updatedSession;

      // Save to database in background
      await sessionService.updateSession(currentUser.uid, sessionId, {
        ActivityMeasurementList: updatedMeasurements,
      });

      console.log("Activity removed successfully");
    } catch (error) {
      console.error("Error removing activity:", error);
      setError("Failed to remove exercise");
      // Note: Not reverting optimistic update to avoid scroll jump - user can manually refresh if needed
    }
  };

  // Handle remove entire activity group
  const handleRemoveActivityGroup = async (indices: number[]) => {
    if (!currentUser?.uid || !sessionId || !session) return;

    try {
      setError(null);

      const indicesToRemove = new Set(indices);
      const updatedMeasurements = (
        session.ActivityMeasurementList || []
      ).filter((_, index) => !indicesToRemove.has(index));

      const updatedSession = {
        ...session,
        ActivityMeasurementList: updatedMeasurements,
      };
      setSession(updatedSession);
      sessionRef.current = updatedSession;

      await sessionService.updateSession(currentUser.uid, sessionId, {
        ActivityMeasurementList: updatedMeasurements,
      });

      console.log("Activity group removed successfully");
    } catch (error) {
      console.error("Error removing activity group:", error);
      setError("Failed to remove exercise group");
    }
  };

  // Handle end session - clean and simple
  const handleEndSession = async () => {
    if (!currentUser?.uid || !sessionId || !session) return;

    try {
      setIsEndingSession(true);
      setError(null);

      console.log("🔚 Ending session...");

      // Step 1: Save all measurements to database
      await saveAllMeasurementsToDatabase();

      // Step 2: Mark session as complete
      console.log("📝 Marking session as complete...");
      await sessionService.completeSession(currentUser.uid, sessionId);
      console.log("✓ Session marked complete");

      // Step 3: Update local session state with complete status
      const updatedSession = {
        ...sessionRef.current!,
        status: "complete" as const,
        endTime: new Date().toISOString(),
      };
      setSession(updatedSession);

      // Step 4: Open training log form with current session data.
      console.log("📊 Opening training log dialog...");
      setIsTrainingLogFormOpen(true);

      console.log("✓ Session ended successfully!");
    } catch (error) {
      console.error("✗ Error ending session:", error);
      setError("Failed to end session");
    } finally {
      setIsEndingSession(false);
    }
  };

  /**
   * Cancel the in-progress attempt (user request): stop the timer, drop any
   * pending saves and this attempt's logged data, then either delete the
   * session (created by pressing Start) or put it back to planned so it
   * reappears on the schedule (coach or self-scheduled beforehand).
   */
  const handleCancelSession = async () => {
    if (!currentUser?.uid || !sessionId || !session) return;
    try {
      setIsCancellingSession(true);
      setError(null);
      debounceTimers.current.forEach((timer) => clearTimeout(timer));
      debounceTimers.current.clear();
      setIsTimerRunning(false);

      if (sessionService.isAdHocSession(session)) {
        await sessionService.deleteSession(currentUser.uid, sessionId);
      } else {
        await sessionService.cancelSessionExecution(
          currentUser.uid,
          sessionId,
          preStartMeasurements.current ?? undefined,
        );
      }
      setIsCancelDialogOpen(false);
      navigate("/dashboard");
    } catch (error) {
      console.error("Error cancelling session:", error);
      setError("Failed to cancel session");
    } finally {
      setIsCancellingSession(false);
    }
  };

  // Handle training log submission
  // Handle training log form close - redirect to dashboard if session is complete
  const handleTrainingLogClose = () => {
    setIsTrainingLogFormOpen(false);
    if (session?.status === "complete") {
      navigate("/dashboard");
    }
  };

  // Calculate initial data for training log form based on session. Duration
  // is the sum of the linked sessions' start–end (one session here; the form
  // re-sums when more are linked, decision 6) and stays editable.
  const getSessionBasedInitialData = () => {
    if (!session || !sessionId) return undefined;
    const durationMinutes = sumSessionDurationMinutes([session]);
    if (durationMinutes <= 0) return undefined;

    return {
      duration: durationMinutes,
      muscular: 1,
      respiratory: 1,
      rpe: 1,
      notes: "",
      tag: [],
      pain: false,
      injured: false,
      load: 0,
      linkedSessionIDs: [sessionId],
      linkedSessionID: sessionId,
    };
  };

  const getActivityTypeName = (measurement: ActivityMeasurement) => {
    const activityType = resolveActivityTypeFromMeasurement(measurement);
    return activityType?.name || "Unknown Exercise";
  };

  const getActivityType = (activityTypeId: string) => {
    return activityTypes.find((type) => type.id === activityTypeId);
  };

  const getCategoryName = (categoryId: string) => {
    // This would need to be implemented if you want to show category names
    // For now, just return empty string
    return "";
  };

  // Handle edit session
  const handleEditSession = () => {
    if (!session || !sessionId) return;
    setIsEditSessionModalOpen(true);
  };

  // Handle session updated
  const handleSessionUpdated = async (updatedSessionId: string) => {
    console.log("Session updated with ID:", updatedSessionId);
    // Refresh session data
    await fetchData();
    setIsEditSessionModalOpen(false);
  };

  // Handle delete click
  const handleDeleteClick = () => {
    if (!session) return;
    setSessionToDelete(session);
  };

  // Handle cancel delete
  const handleCancelDelete = () => {
    setSessionToDelete(null);
    setRejectionReason("");
  };

  // Handle delete session
  const handleDeleteSession = async () => {
    if (!currentUser?.uid || !sessionId || !sessionToDelete) return;

    try {
      setIsDeletingSession(true);
      setError(null);

      // deleteSession unlinks the session from its training log
      // (linkedSessionIDs + legacy scalar), removes it from the day post and
      // deletes the document in one batch. The training log is kept.
      await sessionService.deleteSession(currentUser.uid, sessionId);
      console.log("Session deleted successfully");

      // Navigate back to the previous screen
      navigate(-1);
    } catch (error) {
      console.error("Error deleting session:", error);
      setError("Failed to delete session");
    } finally {
      setIsDeletingSession(false);
      setSessionToDelete(null);
    }
  };

  // Reject a coach-assigned session instead of deleting it, so the coach can
  // still see that the athlete declined the session (and why).
  const handleRejectSession = async () => {
    if (!currentUser?.uid || !sessionId || !sessionToDelete) return;
    if (!rejectionReason.trim()) return;

    try {
      setIsDeletingSession(true);
      setError(null);

      await sessionService.rejectProposedSession(
        currentUser.uid,
        sessionId,
        rejectionReason.trim(),
      );
      console.log("Session rejected successfully");

      navigate(-1);
    } catch (error) {
      console.error("Error rejecting session:", error);
      setError("Failed to reject session");
    } finally {
      setIsDeletingSession(false);
      setSessionToDelete(null);
      setRejectionReason("");
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatSessionDuration = (startTime: string, endTime: string) => {
    try {
      const start = new Date(startTime).getTime();
      const end = new Date(endTime).getTime();
      const durationMs = end - start;
      const totalMinutes = Math.round(durationMs / (1000 * 60));

      if (totalMinutes < 60) {
        return `${totalMinutes} min`;
      } else {
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
      }
    } catch (error) {
      console.error("Error formatting session duration:", error);
      return "Unknown";
    }
  };

  const getStatusColor = sessionStatusChipClass;

  if (loading) {
    return (
      <div className="min-h-screen bg-base-200">
        <Header />
        <div className="pt-16 pb-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="py-8">
              <div className="animate-pulse">
                <div className="h-8 bg-base-300 rounded w-1/3 mb-4"></div>
                <div className="space-y-4">
                  <div className="h-32 bg-base-300 rounded"></div>
                  <div className="h-64 bg-base-300 rounded"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="min-h-screen bg-base-200">
        <Header />
        <div className="pt-16 pb-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="py-8">
              <div className="mb-6">
                <button
                  onClick={() => navigate(-1)}
                  className="flex items-center px-4 py-2 text-base-content/70 bg-base-300 rounded-lg border-base-300 hover:bg-base-300 transition-colors text-sm"
                >
                  <HiArrowLeft className="mr-2" />
                  Back
                </button>
              </div>

              <div className="bg-base-100 rounded-xl shadow p-6">
                <div
                  className="bg-error/10 border border-error/30 rounded-xl p-4"
                  role="alert"
                >
                  <p className="text-error font-grotesk">
                    {error || "Session not found"}
                  </p>
                  <button
                    onClick={fetchData}
                    className="btn btn-ghost btn-sm mt-2 min-h-11 h-11 rounded-xl px-3 text-sm text-error font-grotesk normal-case hover:bg-error/10"
                  >
                    Try again
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  // Calculate progress
  const completedCount =
    session?.ActivityMeasurementList?.filter((m) => m.timeLogged).length || 0;
  const totalCount = session?.ActivityMeasurementList?.length || 0;
  const progressPercentage =
    totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  // Build the ordered list of sections to render. When the coach arranged the
  // session into sections (activityBlocks) — e.g. a Session Note followed by an
  // activity section — render each in order: notes as read-only markdown and
  // activities via a MasterSessionDetails scoped to that section's slice of the
  // flat ActivityMeasurementList (with an indexOffset so logging writes map to
  // the correct global positions). Legacy sessions with no blocks fall back to
  // a single activity list (orderedSections === null).
  type RenderSection =
    | { kind: "notes"; id: string; name: string; content: string }
    | {
        kind: "activities";
        id: string;
        name: string;
        offset: number;
        measurements: ActivityMeasurement[];
      };
  const orderedSections: RenderSection[] | null = (() => {
    const blocks = session?.activityBlocks || [];
    const flat = session?.ActivityMeasurementList || [];
    if (!blocks.length) return null;
    const sections: RenderSection[] = [];
    const usedIndices = new Set<number>();
    blocks.forEach((block) => {
      if (block.blockType === "notes") {
        const content = (block.notesContent || "").trim();
        if (content)
          sections.push({
            kind: "notes",
            id: block.id,
            name: block.name,
            content,
          });
        return;
      }
      const indices: number[] = [];
      flat.forEach((m, i) => {
        if (m.blockId === block.id) {
          indices.push(i);
          usedIndices.add(i);
        }
      });
      if (indices.length === 0) return;
      sections.push({
        kind: "activities",
        id: block.id,
        name: block.name,
        offset: indices[0],
        measurements: indices.map((i) => flat[i]),
      });
    });
    // Any measurements not claimed by a block (e.g. athlete-added, or legacy
    // rows on a partially-migrated session) render in a trailing section.
    const leftover: number[] = [];
    flat.forEach((_, i) => {
      if (!usedIndices.has(i)) leftover.push(i);
    });
    if (leftover.length > 0) {
      sections.push({
        kind: "activities",
        id: "__leftover__",
        name: "Activities",
        offset: leftover[0],
        measurements: leftover.map((i) => flat[i]),
      });
    }
    return sections;
  })();
  const hasActivitySections =
    !orderedSections || orderedSections.some((s) => s.kind === "activities");

  // Collapse state per section. Before the first seed every group renders
  // collapsed (derived); once seeded, sections that appear later (e.g. a
  // trailing athlete-added section) open expanded.
  const FLAT_SECTION_ID = "__flat__";
  const collapsedGroupsFor = (
    sectionId: string,
    measurements: ActivityMeasurement[],
  ): Set<number> =>
    collapsedBySection
      ? (collapsedBySection[sectionId] ?? new Set<number>())
      : initialCollapsedGroups(measurements);
  const setCollapsedGroupsFor = (sectionId: string) => (next: Set<number>) =>
    setCollapsedBySection((prev) => ({ ...(prev ?? {}), [sectionId]: next }));

  // Merge a section's edited measurement slice back into the full list.
  const mergeSection = (offset: number, len: number) => (
    blockList: ActivityMeasurement[],
  ) => {
    const full = [...(sessionRef.current?.ActivityMeasurementList || [])];
    full.splice(offset, len, ...blockList);
    handlePlannedMeasurementsChange(full);
  };

  const sessionTypeName = getSessionTypeName();
  const periodisationLabel = getPeriodisationLabel();

  // Coach-assigned sessions are rejected rather than deleted, so the coach
  // keeps a record of the athlete declining.
  const isCoachAssigned = Boolean(
    session.coachIssued || session.coachID || session.programLinkage,
  );

  return (
    <div className="min-h-screen bg-base-100 pb-8" style={{ isolation: 'isolate' }}>
      <Header />

      {/* Completion Toast */}
      {showCompletionToast && (
        <div className="fixed top-24 left-1/2 transform -translate-x-1/2 z-30 animate-in slide-in-from-top-5 duration-300">
          <div className="bg-success text-success-content px-6 py-3 rounded-xl shadow-lg flex items-center gap-2">
            <HiCheckCircle className="w-5 h-5" />
            <span className="font-grotesk font-medium">
              {showCompletionToast.activityName} logged successfully!
            </span>
          </div>
        </div>
      )}

      <div className="pt-16">
        <div className="max-w-4xl mx-auto sm:px-6">
          <div className="py-8 pb-0">
            {/* Header with Back Button and AI Button */}
            <div className="mb-6 mx-8">
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={() => navigate(-1)}
                  className="p-1 rounded-full hover:bg-base-300 bg-base-300 transition-colors"
                  style={{ touchAction: "manipulation" }}
                >
                  <HiChevronLeft className="h-8 w-8 text-base-content/70" />
                </button>
                {aiLogFunctions.length > 0 && (
                  <AILogButton
                    prompt={aiPrompt}
                    logFunctions={aiLogFunctions}
                    title="Dictate session with AI"
                  />
                )}
              </div>

              {/* Title and Edit Button */}
              <div className="flex items-center gap-3 mb-1">
                <button
                  onClick={handleEditSession}
                  className="flex items-center gap-2 group hover:bg-base-200 rounded-lg px-2 py-1 -ml-2 transition-colors"
                >
                  <div className="min-w-0 text-left">
                    <h1 className="text-2xl font-bold text-base-content font-heading truncate">
                      {getDisplayTitle(sessionTypeName)}
                    </h1>
                    {/* While executing the training type stays in the header
                        (requirement change on plan 3e) so the athlete always
                        sees what they are doing. */}
                    {session.status === "executing" &&
                      sessionTypeName &&
                      getDisplayTitle(sessionTypeName) !== sessionTypeName && (
                        <p className="text-sm font-medium text-warning font-grotesk truncate">
                          {sessionTypeName}
                        </p>
                      )}
                  </div>
                  <HiPencil className="text-base-content/30 group-hover:text-base-content/70 transition-colors flex-shrink-0" />
                </button>
              </div>

              {/* Status Badge (proposed sessions are auto-accepted, so the tag is hidden) */}
              {session.status !== "proposed" && (
                <div className="mb-2 -ml-2 px-2">
                  <span
                    className={`${STATUS_CHIP_BASE} ${getStatusColor(
                      session.status,
                    )}`}
                  >
                    {session.status.charAt(0).toUpperCase() +
                      session.status.slice(1)}
                  </span>
                </div>
              )}

              {/* Periodisation (shown underneath title if available) */}
              {periodisationLabel && (
                <div className="flex items-center gap-2 px-2 py-1 -ml-2 mb-2">
                  <span className="font-grotesk text-base-content/70 text-sm">
                    Periodisation: {periodisationLabel}
                  </span>
                </div>
              )}
              {/* Left Section - Session Info */}
              <div className="flex-1 min-w-0">
                {/* Session Details - Always visible */}
                <div className="flex flex-wrap items-center gap-2 sm:gap-6 text-sm text-base-content/70">
                  {sessionTypeName && (
                    <div className="flex items-center gap-2">
                      <HiTag className="text-base text-base-content/50 flex-shrink-0" />
                      <span className="font-grotesk">{sessionTypeName}</span>
                    </div>
                  )}
                  {session.coachIssued && session.coach?.name && (
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full overflow-hidden bg-base-300 flex-shrink-0">
                        {session.coach?.imageUrlTiny || session.coach?.imageUrl ? (
                          <img
                            src={session.coach.imageUrlTiny || session.coach.imageUrl}
                            alt={session.coach.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-xs font-semibold text-base-content/70">
                            {session.coach.name.charAt(0)}
                          </div>
                        )}
                      </div>
                      <span className="font-grotesk text-base-content/70">
                        Assigned by {session.coach.name}
                      </span>
                    </div>
                  )}
                  {session.status !== "complete" && (
                    <div className="flex items-center gap-2">
                      <HiCalendar className="text-base text-base-content/50 flex-shrink-0" />
                      <span className="font-grotesk">
                        Planned: {formatDate(session.plannedDate)}
                      </span>
                    </div>
                  )}
                  {session.startTime && (
                    <div className="flex items-center gap-2">
                      <HiClock className="text-base text-base-content/50 flex-shrink-0" />
                      <span className="font-grotesk">
                        Started: {formatTime(session.startTime)}
                      </span>
                    </div>
                  )}
                  {session.status === "complete" && (
                    <div className="flex items-center gap-2">
                      <HiCalendar className="text-base text-base-content/50 flex-shrink-0" />
                      <span className="font-grotesk">
                        Completed:{" "}
                        {formatDate(session.endTime || session.plannedDate)}
                        {session.endTime && <> · {formatTime(session.endTime)}</>}
                      </span>
                    </div>
                  )}
                  {session.startTime && session.endTime && (
                    <div className="flex items-center gap-2">
                      <HiClock className="text-base text-base-content/50 flex-shrink-0" />
                      <span className="font-grotesk">
                        Duration:{" "}
                        {formatSessionDuration(
                          session.startTime,
                          session.endTime,
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Session Header - Sticky */}
            <div
              className={`bg-base-100   p-6 ${
                isTimerRunning ? "sticky top-16 z-30" : ""
              }`}
            >
              <div className="space-y-2 gap-2">
                {/* Live timer row (plan 3e): elapsed h:mm:ss left, stop icon
                    right. Stop ends the session and opens the training log
                    form, the same as the previous "End Session" button. */}
                {isTimerRunning && (
                  <div className="flex items-center justify-between gap-4 rounded-xl border border-warning/30 bg-warning/10 px-4 py-3 sm:px-6">
                    <div
                      className="text-3xl sm:text-4xl font-geist font-semibold tabular-nums text-warning"
                      aria-label="Elapsed time"
                      role="timer"
                    >
                      {formatElapsedTime(elapsedTime)}
                    </div>
                    {session.status === "executing" && (
                      <button
                        type="button"
                        onClick={handleEndSession}
                        disabled={isEndingSession}
                        aria-label={
                          isEndingSession ? "Saving session" : "Stop session"
                        }
                        title="Stop session"
                        className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-warning text-warning-content shadow-sm hover:bg-warning/90 active:scale-95 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isEndingSession ? (
                          <HiArrowPath className="h-6 w-6 animate-spin" />
                        ) : (
                          <HiStop className="h-6 w-6" />
                        )}
                      </button>
                    )}
                  </div>
                )}
                <div className="flex items-center gap-4 flex-shrink-0">
                  {(session.status === "planned" ||
                    session.status === "proposed") &&
                    !isTimerRunning && (
                      <>
                        <button
                          onClick={handleStartTimer}
                          disabled={isStartingTimer || isEndingSession}
                          className="btn btn-outline btn-success flex-1 min-h-12 h-12 rounded-xl text-sm font-medium font-grotesk normal-case whitespace-nowrap disabled:opacity-50"
                        >
                          {isStartingTimer ? "Starting..." : "Start Session"}
                        </button>
                        <button
                          onClick={handleEndSession}
                          disabled={isEndingSession || isStartingTimer}
                          className="btn btn-success flex-1 min-h-12 h-12 rounded-xl text-sm font-medium font-grotesk normal-case whitespace-nowrap disabled:opacity-50"
                        >
                          {isEndingSession ? "Saving..." : "Complete & Log"}
                        </button>
                      </>
                    )}

                  {/* Executing sessions are stopped from the timer row above.
                      Fallback for an executing session with no startTime
                      (timer not running) so it can still be ended. */}
                  {session.status === "executing" && !isTimerRunning && (
                    <button
                      onClick={handleEndSession}
                      disabled={isEndingSession}
                      className="flex w-full justify-center items-center gap-2 px-4 py-4 text-sm font-medium text-warning-content bg-warning rounded-xl hover:bg-warning/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-grotesk whitespace-nowrap"
                    >
                      <HiStop className="h-5 w-5" />
                      {isEndingSession ? "Saving..." : "End Session"}
                    </button>
                  )}

                  {session.status === "complete" &&
                    !session.linkedTrainingLogID && (
                      <button
                        onClick={() => setIsTrainingLogFormOpen(true)}
                        className="btn btn-primary btn-block min-h-12 h-12 rounded-xl text-sm font-medium font-grotesk normal-case whitespace-nowrap"
                      >
                        Add to Training Log
                      </button>
                    )}
                </div>
                <div>
                  {/* Coach-assigned sessions are rejected (not deleted) so the
                      coach retains a record. Rejecting a completed session makes
                      no sense, so it's hidden once complete. Athlete's own
                      sessions can still be deleted. */}
                  {isCoachAssigned ? (
                    session.status !== "complete" && (
                      <button
                        onClick={handleDeleteClick}
                        className="btn btn-outline btn-error btn-block min-h-11 h-11 rounded-xl text-sm font-medium font-grotesk normal-case gap-2"
                        title="Reject session"
                      >
                        <HiNoSymbol className="text-base flex-shrink-0" />
                        <span className="truncate">Reject Session</span>
                      </button>
                    )
                  ) : (
                    <button
                      onClick={handleDeleteClick}
                      className="btn btn-outline btn-error btn-block min-h-11 h-11 rounded-xl text-sm font-medium font-grotesk normal-case gap-2"
                      title="Delete session"
                    >
                      <HiTrash className="text-base flex-shrink-0" />
                      <span className="truncate">Delete Session</span>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Activities List */}
            <div className="bg-base-200">
              <div className="space-y-6 bg-base-100">
                {/* Progress Summary - Only show when executing */}
                {session.status === "executing" &&
                  totalCount > 0 &&
                  hasActivitySections && (
                    <div className="mb-6 px-8">
                      <div className="flex items-center justify-between mb-2 pt-4">
                        <h3 className="text-sm font-semibold text-base-content font-heading">
                          Progress
                        </h3>
                        <span className="text-sm text-base-content/80 font-grotesk">
                          <span className="font-geist font-semibold tabular-nums">
                            {completedCount}/{totalCount}
                          </span>{" "}
                          activities completed
                        </span>
                      </div>
                      <progress
                        className="progress progress-success w-full h-3"
                        value={progressPercentage}
                        max={100}
                        aria-label={`${completedCount} of ${totalCount} activities completed`}
                      />
                    </div>
                  )}

                {orderedSections ? (
                  /* Ordered sections: notes + activity sections in coach order */
                  orderedSections.map((sectionItem) =>
                    sectionItem.kind === "notes" ? (
                      <div key={sectionItem.id} className="px-8 pt-2">
                        <div className="bg-base-200 rounded-sm p-4">
                          <h3 className="text-xs font-medium text-base-content/70 font-geist mb-2">
                            Session
                          </h3>
                          <SessionNotesMarkdown content={sectionItem.content} />
                        </div>
                      </div>
                    ) : (
                      <div key={sectionItem.id}>
                        <h3 className="text-lg font-semibold text-base-content font-heading px-8">
                          {sectionItem.name}
                        </h3>
                        <MasterSessionDetails
                          mode="logging"
                          readOnly={false}
                          allowActivitySelection={true}
                          existingSession={session as Session & { id: string }}
                          plannedMeasurements={sectionItem.measurements}
                          indexOffset={sectionItem.offset}
                          collapsedGroups={collapsedGroupsFor(
                            sectionItem.id,
                            sectionItem.measurements,
                          )}
                          onCollapsedGroupsChange={setCollapsedGroupsFor(
                            sectionItem.id,
                          )}
                          onPlannedMeasurementsChange={mergeSection(
                            sectionItem.offset,
                            sectionItem.measurements.length,
                          )}
                          loading={isDeletingMeasurement}
                          error={error}
                          onErrorChange={setError}
                          onVariableUpdate={handleVariableUpdate}
                          onDuplicateSet={handleDuplicateActivity}
                          onRemoveSet={handleRemoveActivity}
                          onRemoveGroup={handleRemoveActivityGroup}
                          onAddSet={handleRecentActivityAdd}
                          onAddActivity={handleAddActivityFromSelector}
                          onBulkAddActivities={
                            handleBulkAddActivitiesFromSelector
                          }
                          hideFloatingAIButton
                        />
                      </div>
                    ),
                  )
                ) : (
                  <>
                    {/* Activities & Sets Header */}
                    <h3 className="text-lg font-semibold text-base-content font-heading px-8">
                      Activities & Sets
                    </h3>
                    <MasterSessionDetails
                      mode="logging"
                      readOnly={false}
                      allowActivitySelection={true}
                      existingSession={session as Session & { id: string }}
                      plannedMeasurements={session.ActivityMeasurementList || []}
                      collapsedGroups={collapsedGroupsFor(
                        FLAT_SECTION_ID,
                        session.ActivityMeasurementList || [],
                      )}
                      onCollapsedGroupsChange={setCollapsedGroupsFor(
                        FLAT_SECTION_ID,
                      )}
                      onPlannedMeasurementsChange={
                        handlePlannedMeasurementsChange
                      }
                      loading={isDeletingMeasurement}
                      error={error}
                      onErrorChange={setError}
                      onVariableUpdate={handleVariableUpdate}
                      onDuplicateSet={handleDuplicateActivity}
                      onRemoveSet={handleRemoveActivity}
                      onRemoveGroup={handleRemoveActivityGroup}
                      onAddSet={handleRecentActivityAdd}
                      onAddActivity={handleAddActivityFromSelector}
                      onBulkAddActivities={handleBulkAddActivitiesFromSelector}
                      hideFloatingAIButton
                      onAIConfigReady={handleAIConfigReady}
                      onAIApplyChanges={handleAIApplyChanges}
                    />
                  </>
                )}

                {/* Cancel the live attempt (below the sets, secondary style). */}
                {session.status === "executing" && (
                  <div className="px-8 pb-8 pt-2">
                    <button
                      type="button"
                      onClick={() => setIsCancelDialogOpen(true)}
                      disabled={isCancellingSession || isEndingSession}
                      className={`${TEXT_BUTTON_CLASS} btn-block`}
                    >
                      <HiNoSymbol className="h-5 w-5" />
                      Cancel session
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={isCancelDialogOpen}
        title="Cancel session?"
        message="Recorded data for this attempt will be discarded."
        confirmLabel={isCancellingSession ? "Cancelling..." : "Cancel session"}
        cancelLabel="Keep going"
        destructive
        loading={isCancellingSession}
        onConfirm={() => void handleCancelSession()}
        onCancel={() => setIsCancelDialogOpen(false)}
      />

      {/* Activity Measurement Modal */}
      {selectedActivityType && !isEditMeasurementModalOpen && (
        <ActivityMeasurementModal
          isOpen={isMeasurementModalOpen}
          onClose={() => {
            setIsMeasurementModalOpen(false);
            setSelectedActivityIndex(-1);
            setSelectedActivityType(null);
          }}
          activityType={selectedActivityType}
          activityIndex={selectedActivityIndex}
          initialVariables={
            session?.ActivityMeasurementList?.[selectedActivityIndex]?.variables
          }
          onMeasurementCreated={handleMeasurementCreated}
        />
      )}

      {/* Edit Activity Measurement Modal */}
      {selectedActivityType && selectedMeasurement && (
        <EditActivityMeasurementModal
          isOpen={isEditMeasurementModalOpen}
          onClose={() => {
            setIsEditMeasurementModalOpen(false);
            setSelectedMeasurement(null);
            setSelectedMeasurementIndex(-1);
            setSelectedActivityType(null);
          }}
          activityType={selectedActivityType}
          measurement={selectedMeasurement}
          measurementIndex={selectedMeasurementIndex}
          onMeasurementUpdated={handleMeasurementUpdated}
        />
      )}

      {/* Delete Measurement Confirmation Dialog */}
      {measurementToDelete && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10000] p-4"
          onClick={handleCancelDeleteMeasurement}
        >
          <div
            className="bg-base-100 rounded-xl shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-base-content mb-4 font-heading">
              Delete Measurement
            </h3>
            <p className="text-base-content/70 mb-6 font-grotesk">
              Are you sure you want to delete the measurement for{" "}
              <strong>
                {getActivityTypeName(measurementToDelete.measurement)}
              </strong>
              ? This will mark the activity as incomplete and can be re-logged.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCancelDeleteMeasurement}
                disabled={isDeletingMeasurement}
                className={TEXT_BUTTON_CLASS}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteMeasurement}
                disabled={isDeletingMeasurement}
                className="btn btn-error min-h-11 h-11 rounded-xl px-4 text-sm font-medium font-grotesk normal-case"
              >
                {isDeletingMeasurement ? "Deleting..." : "Delete Measurement"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Training Log Form */}
      {session && (
        <TrainingLogForm
          open={isTrainingLogFormOpen}
          onClose={handleTrainingLogClose}
          context={{
            dateID: session.plannedDate.replace(/-/g, ""), // YYYY-MM-DD → YYYYMMDD
            sessionId: sessionId,
            sessionMeasurements: session.ActivityMeasurementList || [],
          }}
          initialData={getSessionBasedInitialData()}
          onSaved={async () => {
            await fetchData();
            setIsTrainingLogFormOpen(false);
            navigate("/dashboard");
          }}
        />
      )}

      {/* Edit Session Modal */}
      {session && sessionId && (
        <EditSessionModal
          isOpen={isEditSessionModalOpen}
          onClose={() => setIsEditSessionModalOpen(false)}
          session={{ ...session, id: sessionId }}
          onSessionUpdated={handleSessionUpdated}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {sessionToDelete && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10000] p-4"
          onClick={handleCancelDelete}
        >
          <div
            className="bg-base-100 rounded-xl shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-base-content mb-4 font-heading">
              {isCoachAssigned ? "Reject Session" : "Delete Session"}
            </h3>
            <p className="text-base-content/70 mb-6 font-grotesk">
              {isCoachAssigned ? (
                <>
                  Are you sure you want to reject the session planned for{" "}
                  <strong>{formatDate(sessionToDelete.plannedDate)}</strong>?
                  Your coach will be able to see that you declined it.
                </>
              ) : (
                <>
                  Are you sure you want to delete the session planned for{" "}
                  <strong>{formatDate(sessionToDelete.plannedDate)}</strong>?
                  This action cannot be undone.
                </>
              )}
            </p>
            {isCoachAssigned && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-base-content/80 mb-2 font-grotesk">
                  Reason for declining (required)
                </label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {[
                    "Injured",
                    "Schedule conflict",
                    "Feeling fatigued",
                    "Other",
                  ].map((quickReason) => (
                    <button
                      key={quickReason}
                      type="button"
                      onClick={() => setRejectionReason(quickReason)}
                      className={`px-3 py-2 min-h-9 text-xs font-medium rounded-full border transition-colors font-grotesk ${
                        rejectionReason === quickReason
                          ? "bg-error text-error-content border-error"
                          : "bg-base-200 text-base-content/70 border-base-300 hover:bg-base-300"
                      }`}
                    >
                      {quickReason}
                    </button>
                  ))}
                </div>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Let your coach know why you're declining this session..."
                  rows={3}
                  className="w-full resize-none rounded-xl border border-base-300 bg-base-200 px-3 py-2 text-[16px] sm:text-sm text-base-content placeholder-base-content/50 focus:outline-none focus:ring-2 focus:ring-error/30 font-grotesk"
                />
              </div>
            )}
            <div className="flex justify-between gap-3">
              <button
                onClick={handleCancelDelete}
                disabled={isDeletingSession}
                className={`${TEXT_BUTTON_CLASS} flex-1`}
              >
                Cancel
              </button>
              <button
                onClick={
                  isCoachAssigned ? handleRejectSession : handleDeleteSession
                }
                disabled={
                  isDeletingSession ||
                  (isCoachAssigned && !rejectionReason.trim())
                }
                className="btn btn-error flex-1 min-h-11 h-11 rounded-xl text-sm font-medium font-grotesk normal-case"
              >
                {isDeletingSession
                  ? isCoachAssigned
                    ? "Rejecting..."
                    : "Deleting..."
                  : isCoachAssigned
                    ? "Reject Session"
                    : "Delete Session"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionPage;
